import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SafeIcon } from './UI';
import {
  FiSearch,
  FiFileText,
  FiUsers,
  FiBriefcase,
  FiX
} from 'react-icons/fi';
import { Repository } from '../data/repository';
import { Client, Invoice, ClientJob } from '../types';

type SearchResultType = 'client' | 'invoice' | 'job';

interface SearchResult {
  id: string;
  clientId?: string;
  title: string;
  subtitle: string;
  type: SearchResultType;
  score: number;
}

const normalize = (text: string): string => {
  return String(text || '')
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const calculateFuzzyScore = (query: string, target: string): number => {
  const q = normalize(query);
  const t = normalize(target);
  if (!q || !t) return 0;
  if (t === q) return 1000;
  if (t.startsWith(q)) return 500;

  const qWords = q.split(' ');
  const tWords = t.split(' ');
  let wordMatchCount = 0;
  for (const qWord of qWords) {
    for (const tWord of tWords) {
      if (tWord.startsWith(qWord)) {
        wordMatchCount++;
        break;
      }
    }
  }
  if (wordMatchCount === qWords.length) return 300;
  if (wordMatchCount > 0) return 200 * (wordMatchCount / qWords.length);
  if (t.includes(q)) return 150;

  let qIdx = 0;
  let matchCount = 0;
  for (let i = 0; i < t.length && qIdx < q.length; i++) {
    if (t[i] === q[qIdx]) {
      matchCount++;
      qIdx++;
    }
  }
  if (qIdx === q.length) return 100 * (matchCount / q.length);
  return 0;
};

const searchClients = (
  query: string,
  clients: Client[]
): SearchResult[] => {
  const results: SearchResult[] = [];
  for (const client of clients) {
    const fields = [
      client.name,
      client.companyName || '',
      client.email || '',
      client.phone || ''
    ].filter(Boolean);
    let bestScore = 0;
    for (const field of fields) {
      const score = calculateFuzzyScore(query, field);
      if (score > bestScore) bestScore = score;
    }
    if (bestScore > 0) {
      results.push({
        id: client.id,
        clientId: client.id,
        title: client.name,
        subtitle: client.companyName || client.email || '',
        type: 'client',
        score: bestScore
      });
    }
  }
  return results;
};

const searchInvoices = (
  query: string,
  invoices: Invoice[],
  clients: Client[]
): SearchResult[] => {
  const results: SearchResult[] = [];
  const clientMap = new Map(clients.map(c => [c.id, c]));
  for (const invoice of invoices) {
    const client = clientMap.get(invoice.clientId);
    const fields = [
      invoice.invoiceNumber,
      client?.name || '',
      client?.companyName || '',
      invoice.status,
      String(invoice.total),
      String(invoice.balanceDue)
    ].filter(Boolean);
    let bestScore = 0;
    for (const field of fields) {
      const score = calculateFuzzyScore(query, field);
      if (score > bestScore) bestScore = score;
    }
    if (bestScore > 0) {
      results.push({
        id: invoice.id,
        clientId: invoice.clientId,
        title: invoice.invoiceNumber,
        subtitle: `${client?.name || 'Unknown'} • ${invoice.status} • ${invoice.currency} ${invoice.total.toFixed(2)}`,
        type: 'invoice',
        score: bestScore
      });
    }
  }
  return results;
};

const searchJobs = (
  query: string,
  jobs: ClientJob[],
  clients: Client[]
): SearchResult[] => {
  const results: SearchResult[] = [];
  const clientMap = new Map(clients.map(c => [c.id, c]));
  for (const job of jobs) {
    const client = clientMap.get(job.clientId);
    const fields = [
      job.jobName,
      client?.name || '',
      client?.companyName || '',
      job.jobType,
      job.status
    ].filter(Boolean);
    let bestScore = 0;
    for (const field of fields) {
      const score = calculateFuzzyScore(query, field);
      if (score > bestScore) bestScore = score;
    }
    if (bestScore > 0) {
      results.push({
        id: job.id,
        clientId: job.clientId,
        title: job.jobName,
        subtitle: `${client?.name || 'Unknown'} • ${job.jobType} • ${job.status}`,
        type: 'job',
        score: bestScore
      });
    }
  }
  return results;
};

export const GlobalSearch = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);
  const [allJobs, setAllJobs] = useState<ClientJob[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const loadData = useCallback(async () => {
    if (dataLoaded) return;
    setIsLoading(true);
    try {
      const [clients, invoices, jobs] = await Promise.all([
        Repository.clients.getAll(),
        Repository.invoices.getAll(),
        Repository.jobs.getAll()
      ]);
      setAllClients(clients);
      setAllInvoices(invoices);
      setAllJobs(jobs);
      setDataLoaded(true);
    } catch (error) {
      console.error('Failed to load search data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [dataLoaded]);

  const performSearch = useCallback(() => {
    if (!query.trim() || !dataLoaded) {
      setResults([]);
      return;
    }
    const clientResults = searchClients(query, allClients);
    const invoiceResults = searchInvoices(query, allInvoices, allClients);
    const jobResults = searchJobs(query, allJobs, allClients);
    const allResults = [...clientResults, ...invoiceResults, ...jobResults]
      .sort((a, b) => b.score - a.score)
      .slice(0, 12);
    setResults(allResults);
    setSelectedIndex(0);
  }, [query, dataLoaded, allClients, allInvoices, allJobs]);

  useEffect(() => {
    performSearch();
  }, [performSearch]);

  const openSearch = useCallback(() => {
    loadData();
    setIsOpen(true);
    setQuery('');
    setResults([]);
    setSelectedIndex(0);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [loadData]);

  const closeSearch = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setResults([]);
    setSelectedIndex(0);
  }, []);

  const handleNavigate = useCallback(
    (result: SearchResult) => {
      if (result.type === 'job') {
        navigate('/jobs-documents');
      } else {
        navigate(
          `/clients-products?clientId=${encodeURIComponent(result.clientId || result.id)}`
        );
      }
      closeSearch();
    },
    [navigate, closeSearch]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && results.length > 0) {
        e.preventDefault();
        handleNavigate(results[selectedIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeSearch();
      }
    },
    [results, selectedIndex, handleNavigate, closeSearch]
  );

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openSearch();
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [openSearch]);

  useEffect(() => {
    if (isOpen && overlayRef.current) {
      overlayRef.current.focus();
    }
  }, [isOpen]);

  const getTypeIcon = (type: SearchResultType) => {
    switch (type) {
      case 'client':
        return FiUsers;
      case 'invoice':
        return FiFileText;
      case 'job':
        return FiBriefcase;
    }
  };

  const getTypeColor = (type: SearchResultType) => {
    switch (type) {
      case 'client':
        return 'bg-sky-50 text-sky-700 border-sky-100';
      case 'invoice':
        return 'bg-emerald-50 text-emerald-700 border-emerald-100';
      case 'job':
        return 'bg-indigo-50 text-indigo-700 border-indigo-100';
    }
  };

  return (
    <>
      {/* Mobile search button */}
      <button
        onClick={openSearch}
        className="xl:hidden min-h-11 min-w-11 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label="Search"
      >
        <SafeIcon icon={FiSearch} className="w-5 h-5" />
      </button>

      {/* Desktop search input */}
      <div className="relative w-full max-w-xs hidden xl:block">
        <SafeIcon
          icon={FiSearch}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4"
        />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search..."
          onFocus={openSearch}
          readOnly
          className="w-full pl-9 pr-12 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:bg-white focus:border-brand-500 cursor-pointer"
        />
        <kbd className="absolute right-2.5 top-1/2 -translate-y-1/2 px-1.5 py-0.5 text-[10px] font-semibold text-gray-400 bg-gray-100 border border-gray-200 rounded">
          ⌘K
        </kbd>
      </div>

      {isOpen && (
        <div
          ref={overlayRef}
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          className="fixed inset-0 z-[9999] bg-gray-900/50 backdrop-blur-sm flex items-start justify-center pt-[5vh] md:pt-[15vh]"
          onClick={closeSearch}
        >
          <div
            className="w-full max-w-2xl mx-3 md:mx-0 bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
              <SafeIcon icon={FiSearch} className="text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search clients, invoices, or jobs..."
                className="min-w-0 flex-1 text-base xl:text-sm outline-none"
                autoFocus
              />
              <button
                onClick={closeSearch}
                className="min-h-11 min-w-11 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiX} className="w-4 h-4" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {isLoading && (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  Loading search index...
                </div>
              )}
              {!isLoading && !query.trim() && dataLoaded && (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  Start typing a client name, invoice number, or job name.
                </div>
              )}
              {!isLoading && query.trim() && results.length === 0 && dataLoaded && (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  No matching records found.
                </div>
              )}
              {!isLoading && results.length > 0 && (
                <div className="py-2">
                  {results.map((result, index) => {
                    const Icon = getTypeIcon(result.type);
                    const colorClass = getTypeColor(result.type);
                    const isSelected = index === selectedIndex;
                    return (
                      <button
                        key={`${result.type}-${result.id}`}
                        onClick={() => handleNavigate(result)}
                        className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-colors ${
                          isSelected ? 'bg-brand-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className={`p-2 rounded-lg ${colorClass}`}>
                          <SafeIcon icon={Icon} className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className={`text-sm font-semibold truncate ${
                              isSelected ? 'text-brand-900' : 'text-gray-900'
                            }`}
                          >
                            {result.title}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {result.subtitle}
                          </p>
                        </div>
                        <span
                          className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${colorClass}`}
                        >
                          {result.type}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              <span className="text-[10px] text-gray-400">
                {results.length > 0
                  ? `${results.length} result${results.length !== 1 ? 's' : ''} found`
                  : 'Search clients, invoices & jobs'}
              </span>
              <span className="text-[10px] text-gray-400">
                ↑ ↓ navigate · Enter open · Esc close
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};